import type {
    CompanionRuntimeEvent,
    CompanionSessionState,
    RoomGatewayEvent,
    RoomGatewayQueueEntry,
} from './CompanionRuntime';
import type { ParticipationLayerKind } from './ParticipationLayer';

export interface NormalizedEventBridgeMetadata {
    source?: 'signaling' | 'web' | 'gateway';
    currentEvent?: string;
    roomId?: string;
}

export interface NormalizedEventBridge {
    emitCompanionRuntimeEvent: (
        event: CompanionRuntimeEvent,
        metadata?: NormalizedEventBridgeMetadata,
    ) => void;
    emitRoomGatewayEvent: (
        event: RoomGatewayEvent,
        metadata?: NormalizedEventBridgeMetadata,
    ) => void;
}

export const createNoopNormalizedEventBridge = (): NormalizedEventBridge => ({
    emitCompanionRuntimeEvent: () => undefined,
    emitRoomGatewayEvent: () => undefined,
});

export const createCompanionStateChangedEvent = (
    sessionId: string,
    state: CompanionSessionState,
    timestamp: number = Date.now(),
): CompanionRuntimeEvent => ({
    type: 'session.state_changed',
    sessionId,
    state,
    timestamp,
});

export const createCompanionInputCompletedEvent = (
    sessionId: string,
    transcript: string,
    timestamp: number = Date.now(),
): CompanionRuntimeEvent => ({
    type: 'input.completed',
    sessionId,
    transcript,
    timestamp,
});

export const createCompanionAudioBridgeEvents = (input: {
    sessionId: string;
    audioId: string;
    payload: string;
    timestamp?: number;
    isFinal?: boolean;
}): CompanionRuntimeEvent[] => {
    const timestamp = input.timestamp ?? Date.now();
    const events: CompanionRuntimeEvent[] = [
        {
            type: 'response.audio_chunk',
            sessionId: input.sessionId,
            audioId: input.audioId,
            payload: input.payload,
            timestamp,
        },
    ];

    if (input.isFinal) {
        events.push({
            type: 'response.completed',
            sessionId: input.sessionId,
            timestamp,
        });
    }

    return events;
};

export const createCompanionPlaybackStartedEvent = (
    sessionId: string,
    timestamp: number = Date.now(),
    audioId?: string,
): CompanionRuntimeEvent => ({
    type: 'playback.started',
    sessionId,
    audioId,
    timestamp,
});

export const createCompanionPlaybackCompletedEvent = (
    sessionId: string,
    timestamp: number = Date.now(),
    audioId?: string,
): CompanionRuntimeEvent => ({
    type: 'playback.completed',
    sessionId,
    audioId,
    timestamp,
});

export const createRoomQueueUpdateEvent = (input: {
    roomId: string;
    queue: RoomGatewayQueueEntry[];
    timestamp?: number;
    activeParticipantId?: string;
}): RoomGatewayEvent => ({
    type: 'queue_update',
    roomId: input.roomId,
    timestamp: input.timestamp ?? Date.now(),
    queue: input.queue,
    activeParticipantId: input.activeParticipantId,
});

export const createRoomTurnGrantedEvent = (input: {
    roomId: string;
    participantId: string;
    timestamp?: number;
    expiresAt?: number;
    layer?: ParticipationLayerKind;
}): RoomGatewayEvent => ({
    type: 'turn_granted',
    roomId: input.roomId,
    participantId: input.participantId,
    timestamp: input.timestamp ?? Date.now(),
    expiresAt: input.expiresAt,
    layer: input.layer,
});

export const createRoomTurnEndedEvent = (input: {
    roomId: string;
    reason: string;
    timestamp?: number;
    participantId?: string;
}): RoomGatewayEvent => ({
    type: 'turn_ended',
    roomId: input.roomId,
    reason: input.reason,
    timestamp: input.timestamp ?? Date.now(),
    participantId: input.participantId,
});
